<script setup lang="ts">
/**
 * BrandMark — Logo + 品牌名 + Slogan 三态
 * - xs(16) / sm(20) / md(32) / lg(48) / xl(96) / 2xl(128)
 * - variant=compact 仅 logo+品牌名；full 含 slogan
 */
import { computed } from 'vue'
import { useBrand } from '@/composables/useBrand'

const props = withDefaults(
  defineProps<{
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
    variant?: 'compact' | 'full'
    showSlogan?: boolean
    mono?: boolean
    align?: 'left' | 'center' | 'right'
    /** 强制前景色（2026-06-26 加）
     * - 'auto'：跟随主题（默认）
     * - 'light'：恒黑（用于黄底/白底等浅色背景）
     * - 'dark'：恒白（用于黑底等深色背景） */
    forced?: 'auto' | 'light' | 'dark'
  }>(),
  {
    size: 'md',
    variant: 'compact',
    showSlogan: undefined,
    mono: false,
    align: 'left',
    forced: 'auto'
  }
)

const { name, slogan, logoPath } = useBrand()

const sizeMap = {
  xs: 16,
  sm: 28,
  md: 40,
  lg: 48,
  xl: 112,
  '2xl': 128
} as const

const nameSizeClass = computed(() => {
  const map: Record<string, string> = {
    xs: '',
    sm: '',
    md: 'text-base',
    lg: 'text-xl',
    xl: 'text-3xl',
    '2xl': 'text-5xl'
  }
  return map[props.size] || ''
})

const sloganSizeClass = computed(() => {
  const map: Record<string, string> = {
    xs: '',
    sm: '',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-base',
    '2xl': 'text-lg'
  }
  return map[props.size] || ''
})

const showSloganFinal = computed(() => {
  if (props.showSlogan !== undefined) return props.showSlogan
  if (props.variant === 'compact') return false
  return ['md', 'lg', 'xl', '2xl'].includes(props.size)
})

const layoutClass = computed(() => {
  const alignMap = { left: 'left', center: 'center', right: 'right' }
  return `brand-mark--align-${alignMap[props.align]}`
})
</script>

<template>
  <span
    class="brand-mark"
    :class="[
      layoutClass,
      { 'brand-mark--mono': mono, 'brand-mark--forced-light': forced === 'light', 'brand-mark--forced-dark': forced === 'dark' }
    ]"
  >
    <img
      :src="logoPath"
      :width="sizeMap[size]"
      :height="sizeMap[size]"
      class="brand-mark__logo"
      alt="隐分宝 Logo"
    />
    <span v-if="nameSizeClass" class="brand-mark__name" :class="nameSizeClass">
      <span class="brand-mark__name-main">{{ name }}</span>
      <span v-if="showSloganFinal" class="brand-mark__slogan" :class="sloganSizeClass">
        {{ slogan }}
      </span>
    </span>
  </span>
</template>

<style scoped>
.brand-mark {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  user-select: none;
  line-height: 1;
}

.brand-mark--align-center {
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
}

.brand-mark--align-right {
  justify-content: flex-end;
}

.brand-mark__logo {
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.brand-mark__name {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.brand-mark__name-main {
  font-family: var(--yfb-font-heading);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-display);
  color: var(--yfb-ink);
  text-transform: uppercase;
}

.brand-mark__slogan {
  font-weight: 400;
  color: var(--yfb-ink-soft);
  text-transform: none;
  letter-spacing: 0;
}

.brand-mark--mono .brand-mark__name-main {
  color: var(--yfb-accent);
  text-shadow: 0 0 10px var(--yfb-glow);
}
.brand-mark--mono .brand-mark__slogan {
  color: var(--yfb-ink-soft);
}
.brand-mark--mono .brand-mark__logo {
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  padding: var(--yfb-sp-1);
  background-color: var(--yfb-surface);
}

/* 强制前景：浅色背景用恒深字 */
.brand-mark--forced-light .brand-mark__name-main {
  color: var(--yfb-on-accent);
}
.brand-mark--forced-light .brand-mark__slogan {
  color: var(--yfb-on-accent);
  opacity: 0.75;
}

/* 强制前景：深色背景用恒亮字 */
.brand-mark--forced-dark .brand-mark__name-main {
  color: var(--yfb-panel-fg);
}
.brand-mark--forced-dark .brand-mark__slogan {
  color: var(--yfb-panel-fg);
  opacity: 0.75;
}
</style>